import React from 'react';
import { createPortal } from 'react-dom';
import { Card, Grid, theme } from 'antd';
import type { CardProps } from 'antd';
import { BottomSheet } from './BottomSheet';

const { useBreakpoint } = Grid;

interface DetailsPopupFrameProps {
  /** Ref setter of the Floating UI floating element (desktop popover). */
  floatingRef?: (node: HTMLElement | null) => void;
  /** Floating UI position styles (desktop popover). */
  floatingStyles?: React.CSSProperties;
  /** Hides the desktop popover until Floating UI has measured it. */
  isPositioned?: boolean;
  /** Desktop popover width; always capped to the viewport. */
  width?: number;
  /** Called on backdrop tap and Escape on small screens. */
  onClose: () => void;
  children: React.ReactNode;
}

/** Card layout shared by both modes: header + scrollable body stacked in a column. */
const CARD_LAYOUT: React.CSSProperties = { display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 };
const CARD_BODY_STYLES: CardProps['styles'] = {
  body: { padding: 0, display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }
};

/**
 * Card shell for calendar detail popups (appointments, reminders).
 * - Desktop: a popover anchored to the clicked event by Floating UI, rendered in a portal so
 *   transformed page ancestors (page transitions) cannot offset `position: fixed`.
 * - Small screens (< md): a {@link BottomSheet}, since an anchored popover does not fit next to a ~40px day cell.
 */
export function DetailsPopupFrame({
  floatingRef,
  floatingStyles,
  isPositioned = true,
  width = 440,
  onClose,
  children
}: DetailsPopupFrameProps) {
  const { token } = theme.useToken();
  const screens = useBreakpoint();
  // Breakpoints are empty on the first render; treat that as desktop to avoid a sheet flash.
  const isSheet = screens.md === false;

  if (isSheet) {
    return (
      <BottomSheet onClose={onClose} as={Card} style={CARD_LAYOUT} panelProps={{ styles: CARD_BODY_STYLES, variant: 'outlined' }}>
        {children}
      </BottomSheet>
    );
  }

  return createPortal(
    <Card
      ref={floatingRef}
      role="dialog"
      style={{
        ...floatingStyles,
        ...CARD_LAYOUT,
        visibility: isPositioned ? 'visible' : 'hidden',
        // Above sticky/fixed table columns.
        zIndex: (token.zIndexPopupBase ?? 1000) + 20,
        width: `min(${width}px, calc(100vw - 24px))`,
        maxHeight: 'calc(100vh - 32px)',
        borderRadius: token.borderRadiusLG,
        boxShadow: token.boxShadowSecondary
      }}
      styles={CARD_BODY_STYLES}
      variant="outlined"
    >
      {children}
    </Card>,
    document.body
  );
}
